import { getDownloadURL } from "firebase/storage";
import { useFormik } from "formik";
import React, { useEffect, useId, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import YupPassword from "yup-password";
import axios from "axios";
import {
  User,
  MultiFactorResolver,
  PhoneMultiFactorInfo,
  OAuthProvider,
  SAMLAuthProvider,
  getRedirectResult,
  signInWithRedirect
} from "firebase/auth";
import * as Sentry from "@sentry/react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { HOME_PATH } from "@/features/dashboard/routes";
import {
  ONBOARDING_PATH,
  shouldRedirectToOnboarding
} from "@/features/onboarding/routes";
import { LoadingState } from "../../components/brand";
import { auth } from "../../api/firebaseApi";
import axiosInstance from "../../api/axiosConfig";
import { AuthEmailAPI } from "../../api/authEmailApi";
import { ForeignNationalsAPI } from "../../api/foreignNationals";
import { APIError } from "../../api/utils/errors";
import { setupIntercom } from "../../helpers/helpers";
import { isTransientAuthError } from "../../lib/auth/transientErrors";
import { useGetFilesFromDB } from "../../hooks/useGetFilesFromDB";
import { useGetUserDetails } from "../../hooks/useGetUserDetails";
import { useGetIndividualData } from "../../hooks/useGetIndividualData";
import { useGetLawyerData } from "../../hooks/useGetLawyerData";
import { useLogOut } from "../../hooks/useLogOut";
import { useLogin } from "../../hooks/useLogin";
import { updateActiveClientEmail } from "../../redux/client-case/activeClientSlice";
import { setForeignNationalData } from "../../redux/foreignNational/foreignNationalSlice";
import {
  setIndividualData,
  updateIndividualAvatar
} from "../../redux/individual/individualSlice";
import { individualSelectors } from "../../redux/individual/selectors";
import {
  setLawyerData,
  updateLawyerAvatar
} from "../../redux/lawyer/lawyerSlice";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import { DATABASE } from "../../types/tables-data";
import { useMultiFactorAuth } from "../../hooks/useMultiFactorAuth";
import { casePath } from "../caseIde/routes";
import { useAuthMotion } from "./authMotion";
import {
  EMAIL_PATTERN,
  getLoginHeading,
  getLoginSubtitle,
  getLoginView,
  isSamlProvider,
  RETURN_TO_KEY,
  rememberReturnPath,
  returnPath,
  type LoginModalState,
  type LoginStep,
  type ResolvedAuthConfig,
  type ResolvedEnterpriseProvider
} from "./loginModel";
import { AuthShell } from "./components/AuthShell";
import { AuthHeader } from "./components/AuthHeader";
import { AuthCardFooter, AuthFooterAction } from "./components/AuthCardFooter";
import { AuthButton } from "./components/AuthButton";
import { EmailStep } from "./components/steps/EmailStep";
import { PasswordStep } from "./components/steps/PasswordStep";
import { SsoStep } from "./components/steps/SsoStep";
import { MfaStep } from "./components/steps/MfaStep";
import {
  ForgotPasswordStep,
  ResetSentStep
} from "./components/steps/ForgotPasswordStep";
import { isCompanyContact } from "../companyPortal/api/portalApi";
import { PORTAL_PATH } from "../companyPortal/routes";

/**
 * Sign-in page. Email first: once `/auth/resolve` says how the firm signs in,
 * the email settles into a read-only row and either the password or the firm's
 * SSO providers unfold beneath it. MFA and forgot-password take over the card.
 *
 * The presentation follows the marketing site's login design; the auth flows
 * (password, firm SSO redirect, MFA, password reset, impersonation via custom
 * token, post-login profile resolution) are unchanged.
 */
export const Login = () => {
  const [modalState, setModalState] = useState<LoginModalState>("default");
  const [userRole, setUserRole] = useState("lawyer");
  const [isWrongPassword, setIsWrongPassword] = useState(false);
  const [isManyRequests, setIsManyRequests] = useState(false);
  const [isNoUserFound, setIsNoUserFound] = useState(false);
  const [sentPassword, setSentPassword] = useState("");
  const [mfaResolver, setMFAResolver] = useState<MultiFactorResolver | null>(
    null
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [isMFACodeInvalid, setIsMFACodeInvalid] = useState(false);
  const [mfaError, setMFAError] = useState<string | null>(null);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetEmailInvalid, setIsResetEmailInvalid] = useState(false);
  const [resetEmailError, setResetEmailError] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isResolvingProfile, setIsResolvingProfile] = useState(false);
  const [isCheckingRedirectResult, setIsCheckingRedirectResult] =
    useState(true);
  const [isProcessingSsoRedirect, setIsProcessingSsoRedirect] = useState(() => {
    return sessionStorage.getItem("ssoRedirectInProgress") === "1";
  });

  const [loginStep, setLoginStep] = useState<LoginStep>("email");
  const loginStepRef = useRef<LoginStep>("email");
  useEffect(() => {
    loginStepRef.current = loginStep;
  }, [loginStep]);

  const hasAutoResolvedSsoTestRef = useRef(false);

  const [resolvedAuth, setResolvedAuth] = useState<ResolvedAuthConfig | null>(
    null
  );
  const [isResolvingAuth, setIsResolvingAuth] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const previousPage = returnPath(location.state?.from);
  const [returnTo] = useState(() =>
    rememberReturnPath(location.state?.from, {
      restore: sessionStorage.getItem("ssoRedirectInProgress") === "1"
    })
  );
  const { customToken, customRole, impersonationTenantId } =
    location.state || {};
  const navigate = useNavigate();
  const visaType = useSelector(individualSelectors.selectVisaType);
  const email =
    userRole === "lawyer"
      ? useSelector(lawyerSelectors.selectEmail)
      : useSelector(individualSelectors.selectEmail);
  const uid =
    userRole === "lawyer"
      ? useSelector(lawyerSelectors.selectUid)
      : useSelector(individualSelectors.selectUid);
  const avatarSrc =
    userRole === "lawyer"
      ? useSelector(lawyerSelectors.selectAvatarSrc)
      : useSelector(individualSelectors.selectAvatarSrc);

  // Kept for parity with the profile bootstrap; the value is read by effects below.
  useGetUserDetails();

  const { onSubmitGetFiles, storageFiles, isLoadingGetFiles } =
    useGetFilesFromDB();

  const {
    onSubmitLogin,
    user,
    errorLogin,
    isLoadingLogin,
    loginAs,
    handleMFASuccess
  } = useLogin();

  const handleMFASuccessRef = useRef(handleMFASuccess);
  useEffect(() => {
    handleMFASuccessRef.current = handleMFASuccess;
  }, [handleMFASuccess]);

  useEffect(() => {
    const tenantId = sessionStorage.getItem("ssoTenantId");
    if (tenantId) {
      auth.tenantId = tenantId;
    }

    setIsCheckingRedirectResult(true);
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          setIsProcessingSsoRedirect(true);
          handleMFASuccessRef.current(result.user);
        }
      })
      .catch((error) => {
        // Ignore when there is no redirect result; surface other errors.
        const code =
          typeof error === "object" && error !== null && "code" in error
            ? String((error as { code?: unknown }).code)
            : "";
        if (!code) return;
        Sentry.captureException(error);
        toast.error("SSO sign-in failed", {
          description: "Please try again or contact support@caseblink.com.",
          duration: 6000,
          position: "bottom-left"
        });
      })
      .finally(() => {
        sessionStorage.removeItem("ssoTenantId");
        sessionStorage.removeItem("ssoEmail");
        if (!auth.currentUser) {
          sessionStorage.removeItem("ssoRedirectInProgress");
          setIsProcessingSsoRedirect(false);
        }
        setIsCheckingRedirectResult(false);
      });
  }, []);

  const { onSubmitLogOut } = useLogOut();

  const { onSubmitGetLawyerData, fullLawyerData } = useGetLawyerData();

  const { onSubmitGetIndividualData, individualData } = useGetIndividualData();

  const { initiateLoginVerification, verifyLoginCode } = useMultiFactorAuth();

  let errorToast: { title: string; subtitle?: string } | null = null;

  if (isWrongPassword) {
    errorToast = {
      title: "Incorrect email or password",
      subtitle: "Please check your credentials and try again."
    };
  } else if (isNoUserFound) {
    errorToast = {
      title: "Account not found",
      subtitle:
        "Please check your email address and try again. If you don't have an account, please contact support@caseblink.com."
    };
  } else if (isManyRequests) {
    errorToast = {
      title: "Too many attempts",
      subtitle:
        "For security reasons, we've temporarily locked your account. Please try again later."
    };
  }

  const lastShownErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!errorToast) {
      lastShownErrorRef.current = null;
      return;
    }
    const { title, subtitle } = errorToast;
    const key = `${title}::${subtitle ?? ""}`;
    if (lastShownErrorRef.current === key) return;
    lastShownErrorRef.current = key;

    toast.error(title, {
      description: subtitle,
      duration: 6000,
      position: "bottom-left"
    });
  }, [errorToast]);

  const resolvedUidRef = useRef<string | null>(null);
  // After a transient failure, hold off re-resolving the same sign-in until the
  // user asks to retry; the effect otherwise reruns on every render.
  const [resolveAttempt, setResolveAttempt] = useState(0);
  const pausedResolveRef = useRef<{ user: User; attempt: number } | null>(null);
  useEffect(() => {
    const uidFromUser = user?.uid ?? null;
    if (!uidFromUser) {
      resolvedUidRef.current = null;
      setIsResolvingProfile(false);
      return;
    }
    if (resolvedUidRef.current === uidFromUser) return;
    if (
      pausedResolveRef.current?.user === user &&
      pausedResolveRef.current.attempt === resolveAttempt
    ) {
      return;
    }

    const resolve = async () => {
      setIsResolvingProfile(true);
      let step = "Resolving profile";
      try {
        // Ensure an ID token is available for Firestore + API calls during the
        // immediate post-login transition (especially for custom-token sign-in).
        let isImpersonationSession = false;
        try {
          const tokenUser = auth.currentUser ?? user ?? null;
          await tokenUser?.getIdToken();
          const tokenResult = await tokenUser?.getIdTokenResult();
          isImpersonationSession = Boolean(
            tokenResult &&
              typeof tokenResult.claims === "object" &&
              tokenResult.claims !== null &&
              "impersonation_id" in tokenResult.claims
          );
        } catch {
          // non-fatal; requests may still succeed on retry
        }

        // If a role is explicitly provided (impersonation / magic link), don't fallback.
        if (customRole === "lawyer" || customRole === "individual") {
          setUserRole(customRole);
          localStorage.setItem("loginType", customRole);

          step = `Fetching ${customRole} profile`;
          const found =
            customRole === "lawyer"
              ? await onSubmitGetLawyerData(uidFromUser, {
                  throwOnError: true,
                  tokenUser: auth.currentUser ?? user ?? null
                })
              : await onSubmitGetIndividualData(uidFromUser, {
                  throwOnError: true
                });

          if (!found) {
            toast.error("No profile found", {
              description:
                "We couldn't find your account profile. Please contact support@caseblink.com.",
              duration: 6000,
              position: "bottom-left"
            });
            onSubmitLogOut("profile_missing");
            return;
          }

          // Lawyer avatars are resolved via backend session bootstrap.
          // Individuals still use Storage-based avatar loading.
          if (customRole === "individual") {
            step = "Fetching avatar files";
            await onSubmitGetFiles(
              `${DATABASE.INDIVIDUALS}/avatars/${uidFromUser}/`,
              { throwOnError: false }
            );
          }

          resolvedUidRef.current = uidFromUser;
          return;
        }

        // Default flow: try lawyer first, then fallback to individual.
        step = "Fetching lawyer details";
        const tokenUser = auth.currentUser ?? user ?? null;
        let lawyerFound = await onSubmitGetLawyerData(uidFromUser, {
          logoutOnMissing: false,
          throwOnError: true,
          tokenUser
        });
        if (lawyerFound) {
          setUserRole("lawyer");
          localStorage.setItem("loginType", "lawyer");
          resolvedUidRef.current = uidFromUser;
          return;
        }

        // Company HR contacts sign in here too (firm SSO or a password reset);
        // check before bootstrapping so they never become firm users.
        step = "Checking HR portal access";
        if (await isCompanyContact()) {
          setUserRole("company");
          localStorage.setItem("loginType", "company");
          resolvedUidRef.current = uidFromUser;
          sessionStorage.removeItem("ssoRedirectInProgress");
          setIsProcessingSsoRedirect(false);
          navigate(PORTAL_PATH, { replace: true });
          return;
        }

        // SSO bootstrap: if the user signed in successfully but their Lawyer profile
        // doesn't exist yet, try to create it via the backend then re-fetch.
        step = "Bootstrapping SSO profile";
        try {
          // Never try to bootstrap during impersonation.
          if (isImpersonationSession) {
            throw new Error("bootstrap-skipped");
          }

          const bootstrapUser = auth.currentUser ?? user ?? null;
          const token = await bootstrapUser?.getIdToken(true);
          await axiosInstance.post(
            "/auth/bootstrap",
            {},
            token
              ? { headers: { Authorization: `Bearer ${token}` } }
              : undefined
          );
          lawyerFound = await onSubmitGetLawyerData(uidFromUser, {
            logoutOnMissing: false,
            throwOnError: true,
            tokenUser: bootstrapUser
          });
        } catch (e: unknown) {
          // Ignore bootstrap failures; user may not be eligible or invited.
        }
        if (lawyerFound) {
          setUserRole("lawyer");
          localStorage.setItem("loginType", "lawyer");
          resolvedUidRef.current = uidFromUser;
          return;
        }

        step = "Fetching client details";
        let individualFound = false;
        try {
          individualFound = await onSubmitGetIndividualData(uidFromUser, {
            logoutOnMissing: false,
            throwOnError: true
          });
        } catch (e: unknown) {
          // If the signed-in user can't read Individual docs, treat as "not an individual"
          const code =
            typeof e === "object" && e !== null && "code" in e
              ? String((e as { code?: unknown }).code)
              : "";
          if (code !== "permission-denied") {
            throw e;
          }
        }
        if (individualFound) {
          setUserRole("individual");
          localStorage.setItem("loginType", "individual");
          step = "Fetching avatar";
          await onSubmitGetFiles(
            `${DATABASE.INDIVIDUALS}/avatars/${uidFromUser}/`,
            { throwOnError: false }
          );
          resolvedUidRef.current = uidFromUser;
          return;
        }

        step = "Fetching foreign national details";
        try {
          const foreignNational = await ForeignNationalsAPI.get(uidFromUser);
          if (foreignNational?.uid) {
            setUserRole("client");
            localStorage.setItem("loginType", "client");
            dispatch(
              setForeignNationalData({
                uid: foreignNational.uid,
                firstName: foreignNational.first_name ?? "",
                middleName: foreignNational.middle_name ?? "",
                lastName: foreignNational.last_name ?? "",
                email: foreignNational.email ?? "",
                avatarSrc: ""
              })
            );
            resolvedUidRef.current = uidFromUser;
            return;
          }
        } catch (e: unknown) {
          // A missing record should behave like "not found" (try next), not a fatal error.
          if (e instanceof APIError && e.status === 404) {
            // continue to "No account found" below
          } else {
            throw e;
          }
        }

        toast.error("No account found", {
          description:
            "We couldn't find your account. Please check your email address and try again. If you don't have an account, please contact support@caseblink.com.",
          duration: 6000,
          position: "bottom-left"
        });
        sessionStorage.removeItem("ssoRedirectInProgress");
        setIsProcessingSsoRedirect(false);
        onSubmitLogOut("no_account");
      } catch (err: unknown) {
        Sentry.captureException(err);
        const e = err as { code?: unknown; message?: unknown };
        const code = typeof e?.code === "string" ? e.code : null;
        const message =
          typeof e?.message === "string" ? e.message : "Unknown error";
        const description = `${step}${code ? ` (${code})` : ""}: ${message}`;

        if (isTransientAuthError(err) && user) {
          resolvedUidRef.current = null;
          pausedResolveRef.current = { user, attempt: resolveAttempt };
          toast.error("Can't reach CaseBlink", {
            description: `${description}. You're still signed in.`,
            duration: Infinity,
            position: "bottom-left",
            action: {
              label: "Retry",
              onClick: () => setResolveAttempt((n) => n + 1)
            }
          });
          return;
        }

        toast.error("Oops, something went wrong.", {
          description,
          duration: 8000,
          position: "bottom-left"
        });
        sessionStorage.removeItem("ssoRedirectInProgress");
        setIsProcessingSsoRedirect(false);
        onSubmitLogOut("login_error");
      } finally {
        setIsResolvingProfile(false);
      }
    };

    resolve();
  }, [
    user,
    resolveAttempt,
    customRole,
    onSubmitGetLawyerData,
    onSubmitGetIndividualData,
    onSubmitGetFiles,
    onSubmitLogOut
  ]);

  useEffect(() => {
    const signIn = async () => {
      if (customToken && !loginAs) {
        try {
          if (typeof impersonationTenantId === "string") {
            const tid = impersonationTenantId.trim();
            auth.tenantId = tid || null;
          } else if (impersonationTenantId === null) {
            auth.tenantId = null;
          }
          await onSubmitLogin("blah", "blah", customToken, customRole);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    };

    signIn();
  }, [customToken, impersonationTenantId]);

  useEffect(() => {
    if (
      !isLoadingLogin &&
      !isLoadingGetFiles &&
      storageFiles.length &&
      uid &&
      avatarSrc === ""
    ) {
      storageFiles.forEach((item) => {
        getDownloadURL(item).then((url) => {
          dispatch(
            userRole === "lawyer"
              ? updateLawyerAvatar({ avatarSrc: url, avatarName: item.name })
              : updateIndividualAvatar({
                  avatarSrc: url,
                  avatarName: item.name
                })
          );
        });
      });
    } else if (
      !isLoadingLogin &&
      !isLoadingGetFiles &&
      storageFiles.length === 0 &&
      uid &&
      avatarSrc === ""
    ) {
      dispatch(
        userRole === "lawyer"
          ? updateLawyerAvatar({ avatarSrc: "/", avatarName: "" })
          : updateIndividualAvatar({ avatarSrc: "/", avatarName: "" })
      );
    }
  }, [storageFiles, uid, isLoadingLogin, isLoadingGetFiles]);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // SSO redirect sign-in updates `auth.currentUser`, but our login hook only
    // sets `user` when calling signInWithEmailAndPassword/signInWithCustomToken.
    // Bridge that gap so existing profile-resolution logic can proceed.
    // IMPORTANT: when a custom token is present (impersonation), do not bridge
    // an existing signed-in user into the login resolver. Wait for the custom-token
    // sign-in to complete and set `user` from the login hook.
    if (customToken) return;
    if (currentUser && !user) {
      handleMFASuccessRef.current(currentUser);
    }
  }, [currentUser, user, customToken]);

  useEffect(() => {
    if (fullLawyerData) {
      dispatch(setLawyerData(fullLawyerData));
    }
  }, [fullLawyerData]);

  useEffect(() => {
    if (individualData) {
      dispatch(setIndividualData(individualData));
    }
  }, [individualData]);

  useEffect(() => {
    // Don't block post-login navigation on avatar downloads; that can take a few seconds
    // and causes a confusing "flash" of the login page after SSO redirect.
    //
    // IMPORTANT: keep this effect *after* the Redux sync effects above so global UI
    // (sidebar/settings) reflects the newly signed-in user before we navigate away.
    const hasLawyerProfile = Boolean(fullLawyerData?.uid);
    const hasIndividualProfile = Boolean(individualData?.uid);

    if (
      previousPage &&
      userRole !== "lawyer" &&
      user?.uid &&
      (hasLawyerProfile || hasIndividualProfile)
    ) {
      // `replace` keeps the login page off the history stack so Back from the
      // landing page does not bounce through it.
      navigate(`${previousPage}`, { replace: true });
      return;
    }

    if (user && email && userRole === "lawyer" && hasLawyerProfile) {
      setupIntercom(email, uid);
      sessionStorage.removeItem("ssoRedirectInProgress");
      setIsProcessingSsoRedirect(false);
      if (fullLawyerData?.hasAcceptedTerms) {
        const onboarding = shouldRedirectToOnboarding(
          fullLawyerData.onboardingStatus
        );
        sessionStorage.removeItem(RETURN_TO_KEY);
        navigate(onboarding ? ONBOARDING_PATH : returnTo ?? HOME_PATH, {
          replace: true
        });
      } else {
        navigate("/accept-agreement", { replace: true });
      }
      return;
    }

    if (
      user?.uid &&
      currentUser &&
      email &&
      visaType &&
      userRole === "individual" &&
      hasIndividualProfile
    ) {
      setupIntercom(email, user?.uid);
      sessionStorage.removeItem("ssoRedirectInProgress");
      setIsProcessingSsoRedirect(false);
      navigate(casePath({ individualId: user.uid }), { replace: true });
    }
  }, [
    previousPage,
    user,
    currentUser,
    userRole,
    email,
    uid,
    visaType,
    fullLawyerData,
    individualData,
    navigate
  ]);

  useEffect(() => {
    // Avoid clearing auth state while Firebase is still finalizing an SSO redirect.
    if (isCheckingRedirectResult) return;
    // During impersonation custom-token sign-in, we intentionally allow a brief
    // period where `currentUser` is null while the session is being replaced.
    if (customToken) return;
    if (!currentUser) {
      sessionStorage.removeItem("ssoRedirectInProgress");
      setIsProcessingSsoRedirect(false);
      onSubmitLogOut();
    }
  }, [currentUser, isCheckingRedirectResult, customToken]);

  const handleResolveFirm = async (rawEmail: string) => {
    const email = rawEmail.trim().toLowerCase();
    setIsWrongPassword(false);
    setIsManyRequests(false);
    setIsNoUserFound(false);
    setIsResolvingAuth(true);
    try {
      const response = await axiosInstance.post<ResolvedAuthConfig>(
        "/auth/resolve",
        { email }
      );
      const payload = response.data ?? { lawFirmId: null };
      setResolvedAuth(payload);

      const ssoEnabled = Boolean(payload.ssoEnabled);
      const tenantId = (payload.tenantId ?? null) as string | null;

      if (payload.lawFirmId && ssoEnabled && tenantId) {
        auth.tenantId = tenantId;
        setLoginStep("sso");
      } else {
        auth.tenantId = null;
        setLoginStep("password");
      }
    } catch (e: unknown) {
      Sentry.captureException(e);
      setResolvedAuth({ lawFirmId: null });
      auth.tenantId = null;
      setLoginStep("password");

      const envBaseURL = import.meta.env.REACT_APP_API_BASE_URL || "";
      const isMisconfiguredLocalhost =
        typeof window !== "undefined" &&
        window.location.hostname !== "localhost" &&
        envBaseURL.includes("localhost");
      const description = isMisconfiguredLocalhost
        ? "This environment is pointing at localhost for the API. Update REACT_APP_API_BASE_URL (Vercel env) to your deployed backend (…/api)."
        : axios.isAxiosError(e)
        ? e.response
          ? `Request failed (${e.response.status}). Please try again or contact support@caseblink.com.`
          : "Network error contacting the API. Please try again or contact support@caseblink.com."
        : "Please try again or contact support@caseblink.com.";
      toast.error("Unable to check SSO settings", {
        description,
        duration: 6000,
        position: "bottom-left"
      });
    } finally {
      setIsResolvingAuth(false);
    }
  };

  const handleChangeEmail = () => {
    setResolvedAuth(null);
    setLoginStep("email");
    setShowPassword(false);
    auth.tenantId = null;
  };

  const handlerLogIn = async (email: string, password: string) => {
    setSentPassword(password);
    setIsWrongPassword(false);
    setIsManyRequests(false);
    setIsNoUserFound(false);

    try {
      await onSubmitLogin(email, password);
    } catch (error: any) {
      Sentry.captureException(error);
      if (error.code === "auth/multi-factor-auth-required") {
        const { resolver } = error;
        if (!resolver) {
          Sentry.captureException(new Error("No MFA resolver found in error"));
          return;
        }
        setMFAResolver(resolver);
        setModalState("mfa");
        await initiateLoginVerification(resolver);
      } else if (error.code === "auth/wrong-password") {
        setIsWrongPassword(true);
      } else if (error.code === "auth/too-many-requests") {
        setIsManyRequests(true);
      } else if (error.code === "auth/user-not-found") {
        setIsNoUserFound(true);
      }
    }
  };

  YupPassword(Yup);

  const {
    values,
    handleSubmit,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue
  } = useFormik({
    initialValues: {
      email: "",
      password: ""
    },
    validate: ({ email, password }) => {
      const nextErrors: Record<string, string> = {};
      const trimmedEmail = String(email || "").trim();
      if (!trimmedEmail) {
        nextErrors.email = "Email is required";
      } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
        nextErrors.email = "Invalid email address";
      }

      if (loginStepRef.current === "password") {
        if (!String(password || "").trim()) {
          nextErrors.password = "Password is required";
        }
      }

      return nextErrors;
    },
    onSubmit: ({ email, password }) => {
      if (loginStepRef.current === "password") {
        return handlerLogIn(email, password);
      }
      return handleResolveFirm(email);
    },
    validateOnChange: false,
    validateOnBlur: false
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const emailParam = String(params.get("email") || "").trim();
    const isSsoTest = params.get("sso_test") === "1";
    if (!emailParam) return;

    if (!values.email) {
      setFieldValue("email", emailParam, false);
    }

    if (isSsoTest && !hasAutoResolvedSsoTestRef.current) {
      hasAutoResolvedSsoTestRef.current = true;
      handleResolveFirm(emailParam);
    }
  }, [location.search, setFieldValue, values.email]);

  const handleStartSSO = async (
    provider: ResolvedEnterpriseProvider,
    emailForSession: string
  ) => {
    sessionStorage.setItem("ssoRedirectInProgress", "1");
    setIsProcessingSsoRedirect(true);
    const tenantId = resolvedAuth?.tenantId ?? null;
    if (tenantId) {
      auth.tenantId = tenantId;
      sessionStorage.setItem("ssoTenantId", tenantId);
    }
    sessionStorage.setItem(
      "ssoEmail",
      String(emailForSession || "")
        .trim()
        .toLowerCase()
    );

    const providerId = String(provider.provider_id || "").trim();
    const firebaseProvider = isSamlProvider(provider)
      ? new SAMLAuthProvider(providerId)
      : new OAuthProvider(providerId);

    await signInWithRedirect(auth, firebaseProvider);
  };

  useEffect(() => {
    if (
      errorLogin === "auth/wrong-password" ||
      errorLogin === "auth/invalid-login-credentials"
    ) {
      setIsWrongPassword(true);
    }
    if (errorLogin === "auth/too-many-requests") {
      setIsWrongPassword(false);
      setIsManyRequests(true);
    }
    if (errorLogin === "auth/user-not-found") {
      setIsNoUserFound(true);
    }
  }, [errorLogin]);

  useEffect(() => {
    if (user && user.uid) {
      localStorage.setItem("userId", user.uid);
    }
    if (user && user.uid && userRole === "client") {
      dispatch(updateActiveClientEmail(values.email));
      navigate("/portal", { state: "login" });
    }
  }, [dispatch, navigate, user, userRole, values.email]);

  const mailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const resetEmailRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!values.email && mailRef.current) {
      mailRef.current.focus();
    }
  }, [userRole]);

  useEffect(() => {
    if (isWrongPassword && sentPassword !== values.password) {
      setIsWrongPassword(false);
      setSentPassword("");
    }
  }, [isWrongPassword, values.password]);

  useEffect(() => {
    if (customRole) {
      setUserRole(customRole);
      localStorage.setItem("loginType", customRole);
    } else {
      setUserRole("lawyer");
      localStorage.setItem("loginType", "lawyer");
    }
  }, []);

  useEffect(() => {
    if (isWrongPassword && modalState !== "incorrectPassword") {
      setModalState("incorrectPassword");
    } else if (!isWrongPassword && modalState === "incorrectPassword") {
      setModalState("default");
    }
  }, [isWrongPassword]);

  const handleMFAChallenge = async (
    resolver: MultiFactorResolver,
    code: string
  ) => {
    setIsMFACodeInvalid(false);
    setMFAError(null);
    setIsVerifyingCode(true);

    try {
      const success = await verifyLoginCode(resolver, code);
      if (success) {
        setModalState("default");
        setVerificationCode("");
        if (auth.currentUser) {
          handleMFASuccess(auth.currentUser);
        }
      } else {
        setIsMFACodeInvalid(true);
      }
    } catch (error: any) {
      Sentry.captureException(error);
      if (error.code === "auth/invalid-verification-code") {
        setIsMFACodeInvalid(true);
      } else if (error.code === "auth/code-expired") {
        setMFAError(
          "Verification code has expired. Please refresh the page and request a new one."
        );
      } else {
        setMFAError(
          "An error occurred during verification. Please refresh the page and try again."
        );
      }
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setResetEmail(values.email);
    setIsWrongPassword(false);
    setModalState("forgotPassword");
  };

  const handleCancelForgotPassword = () => {
    setModalState("default");
    setResetEmail("");
    setIsResetEmailInvalid(false);
    setResetEmailError("");
  };

  const handleResetPassword = async () => {
    setIsResetEmailInvalid(false);
    setResetEmailError("");

    if (!resetEmail) {
      setIsResetEmailInvalid(true);
      setResetEmailError("Email is required");
      return;
    }
    if (!EMAIL_PATTERN.test(resetEmail)) {
      setIsResetEmailInvalid(true);
      setResetEmailError("Invalid email address");
      return;
    }

    setIsResettingPassword(true);
    try {
      await AuthEmailAPI.requestPasswordReset(resetEmail.trim());
      setModalState("resetSent");
    } catch {
      toast.error("Couldn't send the reset email. Try again in a moment.", {
        position: "bottom-left"
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCancelMFA = () => {
    setModalState("default");
    setVerificationCode("");
    setMFAError(null);
    setMFAResolver(null);
  };

  // ── Presentation ────────────────────────────────────────────────────────

  const idPrefix = useId();
  const emailId = `${idPrefix}-email`;
  const passwordId = `${idPrefix}-password`;
  const codeId = `${idPrefix}-code`;
  const resetEmailId = `${idPrefix}-reset-email`;

  const { unfold, fade } = useAuthMotion();

  const isIncorrectPassword = modalState === "incorrectPassword";
  const view = getLoginView(modalState, loginStep);
  const mfaPhoneNumber =
    (mfaResolver?.hints[0] as PhoneMultiFactorInfo | undefined)?.phoneNumber ??
    null;
  const heading = getLoginHeading(view);
  const subtitle = getLoginSubtitle(view, {
    phoneNumber: mfaPhoneNumber,
    email: resetEmail
  });

  // Hand focus to whichever field the view opens with.
  useEffect(() => {
    const target =
      view === "email"
        ? mailRef.current
        : view === "password"
        ? passwordRef.current
        : view === "mfa"
        ? codeRef.current
        : view === "forgotPassword"
        ? resetEmailRef.current
        : null;
    // Wait for the unfold to mount the field before focusing it.
    const id = window.requestAnimationFrame(() => {
      target?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [view]);

  const emailLocked = view === "password" || view === "sso";
  const showContinue = view === "email" && values.email.trim().length > 0;
  const showCredentialForm =
    view === "email" || view === "password" || view === "sso";

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (view === "mfa") {
      event.preventDefault();
      if (!isVerifyingCode && mfaResolver) {
        handleMFAChallenge(mfaResolver, verificationCode);
      }
      return;
    }
    if (view === "forgotPassword") {
      event.preventDefault();
      if (!isResettingPassword) handleResetPassword();
      return;
    }
    if (view === "email" && isResolvingAuth) {
      event.preventDefault();
      return;
    }
    handleSubmit(event);
  };

  const showSsoRedirectLoader =
    isProcessingSsoRedirect ||
    (isCheckingRedirectResult &&
      sessionStorage.getItem("ssoRedirectInProgress") === "1");

  if (showSsoRedirectLoader) {
    return (
      <AuthShell hideMark>
        <div className="flex w-full justify-center py-10">
          <LoadingState size="xl" text="Signing you in…" />
        </div>
      </AuthShell>
    );
  }

  const mfaFieldError = isMFACodeInvalid
    ? "Invalid verification code. Please try again."
    : mfaError;

  return (
    <AuthShell>
      <AuthHeader title={heading} subtitle={subtitle} subtitleKey={view} />

      <form
        autoComplete="off"
        noValidate
        onSubmit={handleFormSubmit}
        className="mt-11 flex w-full flex-col"
      >
        {showCredentialForm && (
          <>
            {/* Decoy field: keeps browsers from autofilling the real ones. */}
            <input
              id="fake-input"
              type="text"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ display: "none" }}
            />

            <EmailStep
              id={emailId}
              inputRef={mailRef}
              value={values.email}
              onChange={(event) => {
                if (loginStep !== "email") {
                  handleChangeEmail();
                }
                handleChange(event);
              }}
              onBlur={handleBlur}
              error={touched.email && errors.email ? errors.email : null}
              invalid={isIncorrectPassword}
              locked={emailLocked}
              onEdit={handleChangeEmail}
              onForgotPassword={handleForgotPasswordClick}
              fade={fade}
            />

            {/* Continue appears once there is something to continue with. */}
            <AnimatePresence initial={false}>
              {showContinue && (
                <motion.div
                  key="continue"
                  {...unfold}
                  className="overflow-hidden"
                >
                  <div className="flex justify-end pt-4">
                    <AuthButton
                      type="submit"
                      forward
                      loading={isResolvingAuth}
                      loadingText="Checking…"
                    >
                      Continue
                    </AuthButton>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* The next step unfolds beneath the settled email. */}
            <AnimatePresence initial={false}>
              {emailLocked && (
                <motion.div key={view} {...unfold} className="overflow-hidden">
                  <div className="pt-5">
                    {view === "password" && (
                      <PasswordStep
                        id={passwordId}
                        inputRef={passwordRef}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          !isWrongPassword &&
                          touched.password &&
                          errors.password
                            ? errors.password
                            : null
                        }
                        invalid={isIncorrectPassword}
                        showPassword={showPassword}
                        onToggleShowPassword={() =>
                          setShowPassword((value) => !value)
                        }
                        onForgotPassword={handleForgotPasswordClick}
                        submitting={isLoadingLogin || isResolvingProfile}
                      />
                    )}
                    {view === "sso" && (
                      <SsoStep
                        providers={resolvedAuth?.providers ?? []}
                        enforceSso={Boolean(resolvedAuth?.enforceSso)}
                        onStartSso={(provider) => {
                          handleStartSSO(provider, values.email);
                        }}
                        onUsePassword={() => setLoginStep("password")}
                      />
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {view === "mfa" && (
          <MfaStep
            id={codeId}
            inputRef={codeRef}
            code={verificationCode}
            onChange={(event) => setVerificationCode(event.target.value)}
            error={mfaFieldError}
            verifying={isVerifyingCode}
            onCancel={handleCancelMFA}
          />
        )}

        {view === "forgotPassword" && (
          <ForgotPasswordStep
            id={resetEmailId}
            inputRef={resetEmailRef}
            email={resetEmail}
            onChange={(event) => {
              setResetEmail(event.target.value);
              if (isResetEmailInvalid) {
                setIsResetEmailInvalid(false);
                setResetEmailError("");
              }
            }}
            error={isResetEmailInvalid ? resetEmailError : null}
            sending={isResettingPassword}
            onBack={handleCancelForgotPassword}
          />
        )}

        {view === "resetSent" && (
          <ResetSentStep
            onBack={handleCancelForgotPassword}
            onUseDifferentEmail={() => setModalState("forgotPassword")}
          />
        )}
      </form>

      {view === "email" && (
        <AuthCardFooter lead="Don't have an account?">
          <AuthFooterAction href="mailto:support@caseblink.com">
            Contact Us
          </AuthFooterAction>
        </AuthCardFooter>
      )}
    </AuthShell>
  );
};

export default Login;
